import React, { FC, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@material-ui/core'
import { Edit as EditIcon } from '@material-ui/icons'
import { TolOtacMaintenance } from '../../../../types/tolOperations'
import { PeriodFilterChips } from '../components/PeriodFilterChips'
import {
  ItemPeriodFilter,
  OTAC_PERIOD_FILTER_OPTIONS,
  OTAC_STATUS_LABELS,
  getOtacMaintenanceStatus,
  otacMatchesFilter,
  parseOtacDate,
} from '../shared/periodStatus'

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(8),
  },
}))

interface OtacMaintenanceListPageProps {
  schedules: TolOtacMaintenance[]
  isLoading: boolean
  onOpenEdit: () => void
}

const formatDisplayDate = (dateString: string): string => {
  const date = parseOtacDate(dateString)
  return date ? format(date, 'yyyy/MM/dd HH:mm') : dateString
}

export const OtacMaintenanceListPage: FC<OtacMaintenanceListPageProps> = ({ schedules, isLoading, onOpenEdit }) => {
  const classes = useStyles()
  const [periodFilter, setPeriodFilter] = useState<ItemPeriodFilter>('all')

  const filterCounts = useMemo(
    () => ({
      current: schedules.filter(s => otacMatchesFilter(s, 'current')).length,
      ended: schedules.filter(s => otacMatchesFilter(s, 'ended')).length,
      all: schedules.length,
    }),
    [schedules],
  )

  const filteredSchedules = useMemo(() => schedules.filter(s => otacMatchesFilter(s, periodFilter)), [
    schedules,
    periodFilter,
  ])

  if (isLoading) {
    return (
      <Box className={classes.loadingState}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h5" component="h2">
          OTACメンテナンススケジュール一覧
        </Typography>
        <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={onOpenEdit}>
          登録・編集
        </Button>
      </Box>

      <PeriodFilterChips
        value={periodFilter}
        options={OTAC_PERIOD_FILTER_OPTIONS}
        counts={filterCounts}
        onChange={setPeriodFilter}
      />

      {schedules.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">メンテナンススケジュールが登録されていません</Typography>
          </Box>
        </Paper>
      ) : filteredSchedules.length === 0 ? (
        <Paper>
          <Box className={classes.emptyState}>
            <Typography variant="h6">該当するスケジュールがありません</Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>状態</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>メンテナンス開始日時</TableCell>
                <TableCell>メンテナンス終了日時</TableCell>
                <TableCell>説明</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSchedules.map(schedule => {
                const status = getOtacMaintenanceStatus(schedule)
                return (
                  <TableRow key={schedule.id}>
                    <TableCell>
                      <Chip label={OTAC_STATUS_LABELS[status]} size="small" />
                    </TableCell>
                    <TableCell>{schedule.id}</TableCell>
                    <TableCell>{formatDisplayDate(schedule.maintenanceStartAt)}</TableCell>
                    <TableCell>{formatDisplayDate(schedule.maintenanceEndAt)}</TableCell>
                    <TableCell>{schedule.description || '-'}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
